import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, Eye, Globe, FileText } from "lucide-react";
import api from "../api/client";

function useDebounce(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image: "",
  tags: "",
  seo_title: "",
  seo_description: "",
  substack_url: "",
  status: "draft",
  author: "MB Shelar",
};

export default function AdminBlogEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api
      .get(`/admin/blogs/${id}`)
      .then((r) => {
        const d = r.data;
        setForm({
          ...EMPTY,
          ...d,
          tags: (d.tags || []).join(", "),
          cover_image: d.cover_image || "",
          seo_title: d.seo_title || "",
          seo_description: d.seo_description || "",
          substack_url: d.substack_url || "",
        });
      })
      .catch(() => setError("Could not load post."))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const wordCount = useMemo(
    () => (form.content || "").trim().split(/\s+/).filter(Boolean).length,
    [form.content]
  );
  const readingTime = Math.max(1, Math.round(wordCount / 220));

  const debouncedTitle = useDebounce(form.title, 400);
  useEffect(() => {
    if (!isNew) return;
    if (form.slug) return;
    const auto = (debouncedTitle || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
    if (auto) setForm((f) => ({ ...f, slug: auto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTitle]);

  const submit = async (overrideStatus) => {
    setError("");
    setSaved(false);
    if (!form.title || !form.excerpt || !form.content) {
      setError("Title, excerpt, and content are required.");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      slug: form.slug || undefined,
      excerpt: form.excerpt,
      content: form.content,
      cover_image: form.cover_image || null,
      tags: (form.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      seo_title: form.seo_title || null,
      seo_description: form.seo_description || null,
      substack_url: form.substack_url || null,
      status: overrideStatus || form.status,
      author: form.author || "MB Shelar",
    };
    try {
      if (isNew) {
        const r = await api.post("/admin/blogs", payload);
        navigate(`/admin/blogs/${r.data.id}`, { replace: true });
      } else {
        await api.put(`/admin/blogs/${id}`, payload);
      }
      setSaved(true);
      if (overrideStatus) setForm((f) => ({ ...f, status: overrideStatus }));
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main style={{ background: "#EBEBEB", minHeight: "100vh" }}>
      <header className="border-b border-neutral-200/70 bg-white sticky top-0 z-30">
        <div className="container-x flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/blogs"
              className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-600"
            >
              <ArrowLeft size={16} />
            </Link>
            <div>
              <div className="font-semibold text-neutral-900 text-[15px]">
                {isNew ? "New post" : "Edit post"}
              </div>
              <div className="text-[12px] text-neutral-500">
                {wordCount} words &middot; {readingTime} min read
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreview(!preview)}
              className="px-3 py-2 rounded-full text-[13px] border border-neutral-200 hover:bg-neutral-50 inline-flex items-center gap-1.5"
            >
              <Eye size={14} />
              {preview ? "Edit" : "Preview"}
            </button>
            <button
              onClick={() => submit()}
              disabled={saving}
              className="px-4 py-2 rounded-full text-[13px] font-medium border border-neutral-200 hover:bg-neutral-50 inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <FileText size={14} />
              Save draft
            </button>
            <button
              onClick={() => submit("published")}
              disabled={saving}
              className="px-4 py-2 rounded-full text-[13px] font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
              style={{ background: "var(--agero-orange)", color: "#fff" }}
            >
              <Globe size={14} />
              Publish
            </button>
          </div>
        </div>
        {(error || saved) && (
          <div
            className="container-x py-2 text-[13px]"
            style={{ color: error ? "var(--agero-orange)" : "#15803d" }}
          >
            {error || (saved && "Saved ✓")}
          </div>
        )}
      </header>

      <section className="container-x py-8">
        {loading ? (
          <div className="py-12 text-center text-neutral-500">Loading...</div>
        ) : preview ? (
          <div className="max-w-3xl mx-auto bg-white rounded-3xl p-8 md:p-10 border border-neutral-200/70">
            <h1
              className="font-display"
              style={{
                fontSize: "clamp(28px, 4vw, 48px)",
                letterSpacing: "-0.035em",
                lineHeight: 1.05,
              }}
            >
              {form.title || "Untitled"}
            </h1>
            <p className="mt-4 text-neutral-600">{form.excerpt}</p>
            <div className="prose-blog mt-8">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {form.content}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
            {/* Editor */}
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-neutral-200/70">
              <label className="block text-[12px] font-semibold uppercase tracking-wider text-neutral-500 mb-2">
                Title
              </label>
              <input
                value={form.title}
                onChange={onChange("title")}
                placeholder="Write a great headline..."
                className="w-full bg-transparent border-none outline-none font-display"
                style={{
                  fontSize: "clamp(24px, 3vw, 38px)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.1,
                }}
              />

              <label className="block text-[12px] font-semibold uppercase tracking-wider text-neutral-500 mb-2 mt-6">
                Excerpt
              </label>
              <textarea
                value={form.excerpt}
                onChange={onChange("excerpt")}
                rows={2}
                placeholder="One-line summary that shows up on the blog list and in search results."
                className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition resize-none"
              />

              <label className="block text-[12px] font-semibold uppercase tracking-wider text-neutral-500 mb-2 mt-6">
                Content (Markdown)
              </label>
              <textarea
                value={form.content}
                onChange={onChange("content")}
                rows={22}
                placeholder={"# Heading\n\nWrite your post in markdown. Use **bold**, *italics*, lists, > quotes, and `code`."}
                className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition font-mono text-[14px] leading-relaxed"
              />
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-5">
              <div className="bg-white rounded-3xl p-6 border border-neutral-200/70">
                <div className="text-[12px] font-semibold uppercase tracking-wider text-neutral-500 mb-3">
                  Settings
                </div>
                <label className="block text-[13px] text-neutral-600 mb-1.5">Slug</label>
                <input
                  value={form.slug}
                  onChange={onChange("slug")}
                  placeholder="my-blog-post"
                  className="w-full px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-sm focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                />
                <div className="text-[11px] text-neutral-400 mt-1">
                  /blog/{form.slug || "..."}
                </div>

                <label className="block text-[13px] text-neutral-600 mb-1.5 mt-4">
                  Tags (comma separated)
                </label>
                <input
                  value={form.tags}
                  onChange={onChange("tags")}
                  placeholder="LinkedIn, B2B, Pipeline"
                  className="w-full px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-sm focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                />

                <label className="block text-[13px] text-neutral-600 mb-1.5 mt-4">
                  Cover image URL
                </label>
                <input
                  value={form.cover_image}
                  onChange={onChange("cover_image")}
                  placeholder="https://..."
                  className="w-full px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-sm focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                />

                <label className="block text-[13px] text-neutral-600 mb-1.5 mt-4">
                  Substack URL (optional)
                </label>
                <input
                  value={form.substack_url}
                  onChange={onChange("substack_url")}
                  placeholder="https://thelinkedinlabreports.substack.com/p/..."
                  className="w-full px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-sm focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                />
                <div className="text-[11px] text-neutral-400 mt-1">
                  Paste the URL after you cross-post to Substack.
                </div>

                <label className="block text-[13px] text-neutral-600 mb-1.5 mt-4">Author</label>
                <input
                  value={form.author}
                  onChange={onChange("author")}
                  className="w-full px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-sm focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                />
              </div>

              <div className="bg-white rounded-3xl p-6 border border-neutral-200/70">
                <div className="text-[12px] font-semibold uppercase tracking-wider text-neutral-500 mb-3">
                  SEO
                </div>
                <label className="block text-[13px] text-neutral-600 mb-1.5">
                  SEO title (optional)
                </label>
                <input
                  value={form.seo_title}
                  onChange={onChange("seo_title")}
                  placeholder="Defaults to post title"
                  className="w-full px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-sm focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                />
                <label className="block text-[13px] text-neutral-600 mb-1.5 mt-4">
                  SEO description (optional)
                </label>
                <textarea
                  value={form.seo_description}
                  onChange={onChange("seo_description")}
                  rows={3}
                  placeholder="Defaults to excerpt. Keep under ~160 chars."
                  className="w-full px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-sm focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition resize-none"
                />
                <div className="text-[11px] text-neutral-400 mt-1">
                  {(form.seo_description || form.excerpt || "").length}/160
                </div>
              </div>

              <div
                className="rounded-3xl p-5 text-sm"
                style={{
                  background:
                    form.status === "published" ? "rgba(34,197,94,0.10)" : "rgba(255,51,1,0.10)",
                  color: form.status === "published" ? "#15803d" : "var(--agero-orange)",
                }}
              >
                <span className="font-semibold uppercase tracking-wider text-[11px]">Status:</span>{" "}
                {form.status}
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
